import React, { useCallback, useState } from 'react';

async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}

function ImportStringSection({ section }) {
  const importString = section.importString || '';
  const [copied, setCopied] = useState(false);
  const copiedLabel = section.copiedLabel || 'Copied!';

  const handleCopy = useCallback(async () => {
    if (!importString) return;
    await copyTextToClipboard(importString);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [importString]);

  if (!importString) return null;

  return (
    <div className="trophy-tracker-import-string">
      <SectionHeading heading={section.heading} highlight={section.highlight} />
      {section.intro && <RichBlock text={section.intro} />}
      <button
        type="button"
        className={`tool-import-string-bar${copied ? ' is-copied' : ''}`}
        onClick={handleCopy}
        title="Click to copy Streamer.bot import string"
        aria-label="Copy Streamer.bot import string to clipboard"
      >
        <span className="tool-import-string-text">
          {copied ? copiedLabel : importString}
        </span>
      </button>
      {section.helpText && <p className="tool-import-string-help">{section.helpText}</p>}
    </div>
  );
}

function permissionClass(permission) {
  const key = (permission || '').trim().toUpperCase();
  if (key === 'EVERYONE') return 'is-everyone';
  if (key === 'MOD' || key === 'MODERATOR' || key === 'MODS') return 'is-mod';
  if (key === 'VIP') return 'is-vip';
  if (key === 'SUB' || key === 'SUBSCRIBER' || key === 'SUBS') return 'is-sub';
  if (key === 'BROADCASTER' || key === 'STREAMER') return 'is-broadcaster';
  return 'is-default';
}

function CommandsSection({ section }) {
  const commands = section.commands || [];
  const collapsible = section.collapsible !== false;
  const [open, setOpen] = useState(section.defaultOpen !== false);

  if (!commands.length) return null;

  const panelTitle = section.panelTitle || section.highlight || 'Commands';

  return (
    <div className="trophy-tracker-commands">
      {(section.heading || section.highlight) && (
        <SectionHeading heading={section.heading} highlight={section.highlight} />
      )}
      {section.intro && <RichBlock text={section.intro} />}

      <div className={`animus-commands-panel${open ? ' is-open' : ' is-collapsed'}`}>
        <div className="animus-commands-panel__chrome" aria-hidden="true">
          <span className="animus-commands-panel__bracket animus-commands-panel__bracket--tl" />
          <span className="animus-commands-panel__bracket animus-commands-panel__bracket--br" />
        </div>

        <div className="animus-commands-panel__head">
          <div className="animus-commands-panel__title">
            <span className="animus-commands-panel__icon" aria-hidden="true">!</span>
            <span>{panelTitle}</span>
          </div>
          {collapsible && (
            <button
              type="button"
              className="animus-commands-panel__toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={open ? 'Collapse commands' : 'Expand commands'}
            >
              <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
            </button>
          )}
        </div>

        {open && (
          <div className="animus-commands-panel__body">
            <div className="animus-commands-table-wrap">
              <table className="animus-commands-table">
                <thead>
                  <tr>
                    <th>Command</th>
                    <th>Permission</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  {commands.map((cmd, i) => (
                    <tr key={i}>
                      <td data-label="Command">
                        <span className="animus-command-chip font-mono">{cmd.command}</span>
                      </td>
                      <td data-label="Permission">
                        <span className={`animus-permission-badge ${permissionClass(cmd.permission)}`}>
                          {cmd.permission || 'EVERYONE'}
                        </span>
                      </td>
                      <td data-label="Description">{cmd.description}</td>
                      <td data-label="Example">
                        {cmd.example ? (
                          <span className="animus-command-chip animus-command-chip--example font-mono">{cmd.example}</span>
                        ) : (
                          <span className="animus-command-empty">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function RichBlock({ text, as: Tag = 'p' }) {
  if (!text) return null;
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return <Tag className="tool-rich-content" dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return <Tag className="tool-rich-content">{text}</Tag>;
}

function normalizeIcon(icon) {
  if (!icon) return null;
  const trimmed = icon.trim();
  if (!trimmed) return null;
  if (trimmed.includes(' ')) return trimmed;
  return `fas ${trimmed.startsWith('fa-') ? trimmed : `fa-${trimmed}`}`;
}

function resolveButtonVariant(btn) {
  if (btn.variant) return btn.variant;
  return btn.primary ? 'primary' : 'secondary';
}

function buildButtonStyle(btn) {
  const style = btn.style || {};
  const cssVars = {};
  if (style.background) cssVars['--btn-bg'] = style.background;
  if (style.color) cssVars['--btn-color'] = style.color;
  if (style.borderColor) cssVars['--btn-border'] = style.borderColor;
  if (style.hoverBackground) cssVars['--btn-bg-hover'] = style.hoverBackground;
  return Object.keys(cssVars).length ? cssVars : undefined;
}

function getButtonClassName(btn) {
  const variant = resolveButtonVariant(btn);
  if (variant === 'custom') return 'btn btn-custom';
  if (variant === 'beta') return 'btn btn-beta';
  if (variant === 'primary') return 'btn btn-primary';
  return 'btn btn-secondary';
}

function CtaButton({ btn }) {
  const cls = getButtonClassName(btn);
  const inlineStyle = buildButtonStyle(btn);
  const iconClass = normalizeIcon(btn.icon);
  const isExternal = btn.external || btn.href?.startsWith('http');
  const linkProps = {
    href: btn.href,
    className: cls,
    style: inlineStyle,
    download: btn.download || undefined,
    ...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
  };

  return (
    <a {...linkProps}>
      {iconClass && <i className={iconClass}></i>}
      {btn.text}
    </a>
  );
}

export function SectionHeading({ heading, highlight }) {
  if (!heading) return null;
  return (
    <h2>
      {heading}{highlight ? <> <span className="highlight">{highlight}</span></> : null}
    </h2>
  );
}

export function ToolSectionRenderer({ section }) {
  switch (section.type) {
    case 'description':
      return (
        <div className="trophy-tracker-description">
          <SectionHeading heading={section.heading} highlight={section.highlight} />
          {(section.paragraphs || []).map((p, i) => <RichBlock key={i} text={p} />)}
        </div>
      );

    case 'video':
      return (
        <div className="trophy-tracker-video">
          <SectionHeading heading={section.heading} highlight={section.highlight} />
          {section.youtubeId && (
            <div className="video-embed-container">
              <iframe
                width="560"
                height="315"
                src={`https://www.youtube.com/embed/${section.youtubeId}?rel=0`}
                title={section.title || 'Video tutorial'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          )}
        </div>
      );

    case 'platformVideos':
      return (
        <div className="trophy-tracker-install">
          <SectionHeading heading={section.heading} highlight={section.highlight} />
          {section.intro && <RichBlock text={section.intro} />}
          <div className="achievement-platform-videos">
            {(section.platforms || []).map((platform, i) => (
              <div className="achievement-platform-video-card" key={i}>
                <h3>
                  {platform.name}
                  {platform.highlight && <> <span className="highlight">{platform.highlight}</span></>}
                </h3>
                <div className={`video-embed-container achievement-platform-embed${platform.comingSoon ? ' achievement-platform-coming-soon' : ''}`}>
                  {platform.comingSoon ? (
                    <div className="achievement-platform-coming-soon-content">
                      <i className="fas fa-clock"></i>
                      <p>Coming Soon</p>
                    </div>
                  ) : platform.youtubeId ? (
                    <iframe
                      width="560"
                      height="315"
                      src={`https://www.youtube.com/embed/${platform.youtubeId}?rel=0`}
                      title={`${platform.name} tutorial`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'steps':
      return (
        <div className="trophy-tracker-install">
          <SectionHeading heading={section.heading} highlight={section.highlight} />
          {section.intro && <RichBlock text={section.intro} />}
          {(section.steps || []).map((step, idx) => (
            <div className="install-step" key={idx}>
              <div className="step-number">{idx + 1}</div>
              <div className="step-content">
                {step.title && <h3 dangerouslySetInnerHTML={{ __html: step.title }} />}
                {(step.paragraphs || []).map((p, i) => <RichBlock key={i} text={p} />)}
                {(step.images || []).map((img, i) => (
                  <div className="step-screenshot" key={i}>
                    <img src={img.src} alt={img.alt || ''} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );

    case 'cta':
      return (
        <div className="trophy-tracker-cta">
          <SectionHeading heading={section.heading} highlight={section.highlight} />
          <div className="cta-buttons">
            {(section.buttons || []).map((btn, i) => (
              <CtaButton key={i} btn={btn} />
            ))}
          </div>
          {section.beta?.href && (
            <div className="beta-section">
              {section.beta.description && (
                <p className="beta-description">
                  <i className="fas fa-info-circle"></i>{' '}
                  {/<[a-z][\s\S]*>/i.test(section.beta.description) ? (
                    <span className="tool-rich-content" dangerouslySetInnerHTML={{ __html: section.beta.description }} />
                  ) : (
                    section.beta.description
                  )}
                </p>
              )}
              <CtaButton btn={{
                href: section.beta.href,
                text: section.beta.text || 'Open Beta',
                icon: section.beta.icon || 'fas fa-flask',
                variant: section.beta.variant || 'beta',
                external: true,
                style: section.beta.style,
              }} />
            </div>
          )}
        </div>
      );

    case 'faq':
      return (
        <div className="trophy-tracker-faq">
          <SectionHeading heading={section.heading} highlight={section.highlight} />
          <div className="faq-container">
            {(section.items || []).map((item, i) => (
              <div className="faq-item" key={i}>
                <h3>{item.question}</h3>
                {item.isList ? (
                  <ul className="tool-rich-content">
                    {(item.listItems || []).map((li, j) => (
                      /<[a-z][\s\S]*>/i.test(li) ? (
                        <li key={j} dangerouslySetInnerHTML={{ __html: li }} />
                      ) : (
                        <li key={j}>{li}</li>
                      )
                    ))}
                  </ul>
                ) : (
                  <RichBlock text={item.answer} />
                )}
              </div>
            ))}
          </div>
        </div>
      );

    case 'importString':
      return <ImportStringSection section={section} />;

    case 'commands':
      return <CommandsSection section={section} />;

    default:
      return null;
  }
}
